/**
 * Synthesize 20 Arabic mock rows for demo entities when JSON seeds are missing.
 */

const COUNT = 20;
const CITIES = ['الرياض', 'جدة', 'الدمام'];
const FIRST = ['محمد', 'أحمد', 'خالد', 'سعود', 'فهد', 'نورة', 'سارة', 'ريم', 'فيصل', 'منى'];
const LAST = ['العتيبي', 'القحطاني', 'الدوسري', 'الحربي', 'الزهراني', 'المطيري', 'الغامدي', 'السبيعي', 'الشمري', 'العنزي'];

const pad = (n, w = 3) => String(n).padStart(w, '0');
const arName = (i) => `${FIRST[(i - 1) % FIRST.length]} ${LAST[(i - 1) % LAST.length]}`;
const city = (i) => CITIES[(i - 1) % CITIES.length];
const now = () => new Date().toISOString();
const date = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
};

function baseRow(i, prefix) {
  return {
    id: `${prefix}-${pad(i)}`,
    created_at: now(),
    updated_at: now(),
    createdAt: now(),
    updatedAt: now(),
    status: ['active', 'pending', 'approved', 'completed'][i % 4],
  };
}

const ENTITY_GENERATORS = {
  products: (i) => ({
    ...baseRow(i, 'PRD-MOCK'),
    name: `منتج تجريبي ${i}`,
    reference: `REF-${pad(i)}`,
    sku: `SKU-${pad(i)}`,
    category: `تصنيف ${((i - 1) % 5) + 1}`,
    type: 'product',
    unit: 'قطعة',
    price: 100 + i * 25,
    cost_price: 70 + i * 15,
    tax_rate: 15,
    stock: 50 + i,
    status: 'active',
  }),
  services: (i) => ({
    ...baseRow(i, 'SRV-MOCK'),
    name: `خدمة تجريبية ${i}`,
    reference: `SRV-${pad(i)}`,
    type: 'service',
    unit: 'ساعة',
    price: 200 + i * 30,
    tax_rate: 15,
    status: 'active',
  }),
  categories: (i) => ({
    ...baseRow(i, 'CAT-MOCK'),
    category_number: `CAT-${pad(i)}`,
    name: `تصنيف ${i}`,
    description: `وصف التصنيف ${i}`,
    type: i % 2 === 0 ? 'product' : 'service',
    active: true,
  }),
  clients: (i) => ({
    ...baseRow(i, 'CLI-MOCK'),
    client_number: `CLI-${pad(i)}`,
    company_name: `شركة العميل ${i}`,
    contact_person: arName(i),
    email: `client${i}@example.sa`,
    phone1: `05${String(10000000 + i).slice(0, 8)}`,
    city: city(i),
    address: `حي الأعمال ${i}، ${city(i)}`,
    client_type: i % 3 === 0 ? 'company' : 'entreprise',
    status: 'active',
  }),
  suppliers: (i) => ({
    ...baseRow(i, 'SUP-MOCK'),
    company_name: `مورد ${i}`,
    contact_person: arName(i),
    email: `supplier${i}@example.sa`,
    phone1: `05${String(20000000 + i).slice(0, 8)}`,
    city: city(i),
    status: 'active',
  }),
  employees: (i) => ({
    ...baseRow(i, 'EMP-MOCK'),
    mat: `EMP-${pad(i)}`,
    nom: LAST[(i - 1) % LAST.length],
    prenom: FIRST[(i - 1) % FIRST.length],
    email: `emp${i}@company.sa`,
    telephone: `05${String(30000000 + i).slice(0, 8)}`,
    ville: city(i),
    fonction: ['محاسب', 'مبيعات', 'مخزون', 'موارد بشرية', 'تقنية'][i % 5],
    status: 'active',
  }),
  documents: (i) => ({
    ...baseRow(i, 'DOC-MOCK'),
    number: `INV-2026-${pad(i)}`,
    type: i % 2 === 0 ? 'invoice' : 'delivery_note',
    client: `شركة العميل ${i}`,
    date: date(i),
    amount: 1000 + i * 150,
    status: ['draft', 'pending', 'paid', 'sent'][i % 4],
    title: i % 2 === 0 ? `فاتورة ${i}` : `بون تسليم ${i}`,
  }),
  inventory_warehouses: (i) => ({
    ...baseRow(i, 'WH-MOCK'),
    code: `WH-${pad(i)}`,
    name: `مستودع ${city(i)} ${i}`,
    city: city(i),
    address: `منطقة صناعية ${i}، ${city(i)}`,
    status: 'active',
  }),
  inventory_stock_movements: (i) => ({
    ...baseRow(i, 'MOV-MOCK'),
    reference: `MOV-${pad(i)}`,
    movement_type: i % 2 === 0 ? 'in' : 'out',
    product_name: `منتج ${i}`,
    quantity: 5 + i,
    warehouse_name: `مستودع ${city(i)}`,
    movement_date: date(i),
  }),
  inventory_transfers: (i) => ({
    ...baseRow(i, 'TRF-MOCK'),
    transfer_number: `TRF-${pad(i)}`,
    from_warehouse: `مستودع ${city(i)}`,
    to_warehouse: `مستودع ${city((i % 3) + 1)}`,
    status: ['pending', 'in_transit', 'completed'][i % 3],
    transfer_date: date(i),
  }),
  inventory_valuations: (i) => ({
    ...baseRow(i, 'VAL-MOCK'),
    product_name: `منتج ${i}`,
    warehouse_name: `مستودع ${city(i)}`,
    quantity: 20 + i,
    unit_cost: 50 + i * 2,
    total_value: (20 + i) * (50 + i * 2),
    valuation_date: date(i),
  }),
  achats_purchase_orders: (i) => ({
    ...baseRow(i, 'PO-MOCK'),
    order_number: `PO-2026-${pad(i)}`,
    supplier_name: `مورد ${i}`,
    order_date: date(i),
    total_amount: 5000 + i * 200,
    status: ['pending', 'confirmed', 'received'][i % 3],
  }),
  achats_demandes: (i) => ({
    ...baseRow(i, 'PR-MOCK'),
    request_number: `DA-${pad(i)}`,
    product_name: `مادة ${i}`,
    requester_name: arName(i),
    quantity: 10 + i,
    status: ['pending', 'approved', 'rejected'][i % 3],
    request_date: date(i),
  }),
  ventes_sales_orders: (i) => ({
    ...baseRow(i, 'SO-MOCK'),
    order_number: `SO-2026-${pad(i)}`,
    customer_name: `شركة العميل ${i}`,
    order_date: date(i),
    total_amount: 8000 + i * 250,
    status: ['pending', 'confirmed', 'delivered'][i % 3],
  }),
  crm_leads: (i) => ({
    ...baseRow(i, 'LEAD-MOCK'),
    lead_ref: `LED-${pad(i)}`,
    name: arName(i),
    company: `شركة ${i}`,
    email: `lead${i}@example.sa`,
    phone: `05${String(40000000 + i).slice(0, 8)}`,
    status: ['new', 'contacted', 'qualified'][i % 3],
    estimated_value: 10000 + i * 500,
  }),
  crm_opportunities: (i) => ({
    ...baseRow(i, 'OPP-MOCK'),
    opportunity_ref: `OPP-${pad(i)}`,
    title: `فرصة بيع ${i}`,
    client_name: `شركة ${i}`,
    stage: ['prospection', 'negotiation', 'won'][i % 3],
    amount: 15000 + i * 750,
    probability: 20 + (i % 5) * 15,
  }),
  crm_campaigns: (i) => ({
    ...baseRow(i, 'CMP-MOCK'),
    campaign_ref: `CMP-${pad(i)}`,
    name: `حملة ${i}`,
    type: ['email', 'sms', 'event'][i % 3],
    start_date: date(i + 30),
    end_date: date(i),
    status: ['planned', 'active', 'completed'][i % 3],
  }),
  crm_interactions: (i) => ({
    ...baseRow(i, 'INT-MOCK'),
    interaction_ref: `INT-${pad(i)}`,
    subject: `متابعة عميل ${i}`,
    client_name: `شركة ${i}`,
    interaction_type: ['call', 'email', 'meeting'][i % 3],
    interaction_date: date(i),
    notes: `ملاحظات التفاعل ${i}`,
  }),
  hr_leaves: (i) => ({
    ...baseRow(i, 'LV-MOCK'),
    employee_name: arName(i),
    leave_type: ['إجازة سنوية', 'إجازة مرضية', 'إجازة تعويضية'][i % 3],
    start_date: date(i + 10),
    end_date: date(i),
    days: 3 + (i % 5),
    status: ['pending', 'approved', 'rejected'][i % 3],
  }),
  api_approbations_workflows: (i) => ({
    ...baseRow(i, 'WF-MOCK'),
    name: `مسار اعتماد ${i}`,
    description: `وصف مسار الاعتماد ${i}`,
    status: 'active',
  }),
  api_approbations_requests: (i) => ({
    ...baseRow(i, 'REQ-MOCK'),
    title: `طلب اعتماد ${i}`,
    requester_name: arName(i),
    status: ['pending', 'approved', 'rejected'][i % 3],
    submitted_at: date(i),
  }),
  banking_accounts: (i) => ({
    ...baseRow(i, 'BNK-MOCK'),
    account_number: `SA${String(1000000000000000000 + i)}`,
    bank_name: ['الراجحي', 'الأهلي', 'الرياض', 'الإنماء'][i % 4],
    account_name: `حساب ${i}`,
    balance: 50000 + i * 1000,
    currency: 'SAR',
  }),
};

/** Match entity bucket to a generator (exact or suffix). */
const ENTITY_ALIASES = {
  facturation_products: 'products',
  facturation_services: 'services',
  facturation_services_: 'services',
  facturation_categories: 'categories',
  articles_units_of_measure: 'categories',
  api_articles_unites_mesure: 'categories',
  achats_purchase_orders: 'achats_purchase_orders',
  achats_demandes: 'achats_demandes',
  achats_receptions: 'achats_purchase_orders',
  ventes_sales_orders: 'ventes_sales_orders',
  ventes_livraisons: 'ventes_sales_orders',
  ventes_sales_deliveries: 'ventes_sales_orders',
  crm_leads: 'crm_leads',
  crm_opportunities: 'crm_opportunities',
  crm_campaigns: 'crm_campaigns',
  crm_interactions: 'crm_interactions',
  hr_leaves: 'hr_leaves',
  hr_leaves_: 'hr_leaves',
  employees: 'employees',
  hr_employees: 'employees',
  documents: 'documents',
  facturation_documents: 'documents',
  payments: 'documents',
  facturation_payments: 'documents',
  inventory_warehouses: 'inventory_warehouses',
  inventory_stock_movements: 'inventory_stock_movements',
  inventory_transfers: 'inventory_transfers',
  inventory_valuations: 'inventory_valuations',
  banking_accounts: 'banking_accounts',
  bank_accounts: 'banking_accounts',
  api_approbations_workflows: 'api_approbations_workflows',
  api_approbations_requests: 'api_approbations_requests',
  suppliers: 'suppliers',
  facturation_fournisseurs: 'suppliers',
};

function resolveGenerator(entity) {
  const alias = ENTITY_ALIASES[entity] || entity;
  if (ENTITY_GENERATORS[alias]) return ENTITY_GENERATORS[alias];
  if (ENTITY_GENERATORS[entity]) return ENTITY_GENERATORS[entity];
  const key = Object.keys(ENTITY_GENERATORS).find(
    (k) => entity === k || entity.endsWith(`_${k}`) || entity.includes(k),
  );
  return key ? ENTITY_GENERATORS[key] : null;
}

/** Generic fallback row for unknown list entities. */
function genericRow(entity, i) {
  const prefix = entity.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase() || 'MOCK';
  return {
    ...baseRow(i, prefix),
    name: `عنصر ${i}`,
    title: `عنصر ${i}`,
    reference: `${prefix}-${pad(i)}`,
    description: `بيانات تجريبية ${i}`,
    label: `عنصر ${i}`,
    city: city(i),
  };
}

export function synthesizeMockRows(entity, existing = [], targetCount = COUNT) {
  if (!entity || entity === 'settings') return existing;
  const current = Array.isArray(existing) ? existing : [];
  if (current.length >= targetCount) return current;

  const gen = resolveGenerator(entity) || ((i) => genericRow(entity, i));
  const rows = [...current];
  for (let i = current.length + 1; i <= targetCount; i += 1) {
    rows.push(gen(i));
  }
  return rows;
}

export function synthesizeAllMockData(data, targetCount = COUNT) {
  const out = { ...data };
  for (const [entity, val] of Object.entries(out)) {
    if (entity === 'settings' || val == null) continue;
    if (Array.isArray(val)) {
      out[entity] = synthesizeMockRows(entity, val, targetCount);
    }
  }
  return out;
}
